// src/utils/sourceAdapters.js
//
// Pass 64 — SOURCE ADAPTERS. Each adapter maps ONE source's raw shape → the SAME normalized
// SourceIntakeEnvelope (via normalizeSourceIntake). This is the ONLY place source-specific knowledge
// is allowed. The canonical core (bookingCase / clientIdentity / scheduling) never sees Gmail HTML,
// message ids, partner reservation fields, or spreadsheet rows.
//
//   manualAdapter        — the real PROOF adapter (operator paste). No external id → caller generates one.
//   emailFixtureAdapter  — realistic email shape (messageId/threadId/from/subject/body) for a future Gmail connector.
//   partnerApiFixtureAdapter — realistic partner/API reservation payload for a future webhook connector.
//
// All three call normalizeSourceIntake — proving future GmailAdapter/WhatsAppAdapter/RoomBossAdapter/
// HotelAdapter/PartnerAdapter/PublicCheckoutAdapter plug into the identical boundary.

import { normalizeSourceIntake, toPlainText, SOURCE_SYSTEM, SOURCE_TYPE } from './sourceIntake';

const str = (v) => String(v == null ? '' : v).trim();

// A very conservative, DETERMINISTIC draft extractor. It only reads dates it can parse unambiguously
// (ISO `YYYY-MM-DD`), and an explicit "N instructor(s)" count. It NEVER guesses a year, AM/PM, timezone,
// discipline, or group size. Ambiguous/none → a single needsReview draft line (operator supplies truth).
export function extractRequestedSessionsDraft(text = '') {
  const t = toPlainText(text);
  const lines = [];
  const isoDates = [...t.matchAll(/\b(20\d{2})-(\d{2})-(\d{2})\b/g)].map((m) => m[0]);
  const instrMatch = t.match(/\b(\d{1,2})\s*(?:ski|snowboard)?\s*instructors?\b/i);
  const instructorsRequired = instrMatch ? Math.max(1, Math.min(20, parseInt(instrMatch[1], 10))) : 1;
  const discipline = /snowboard/i.test(t) ? 'snowboard' : (/\bski\b/i.test(t) ? 'ski' : '');
  const uniqueDates = [...new Set(isoDates)];
  if (uniqueDates.length) {
    for (const d of uniqueDates) lines.push({ date: d, instructorsRequired, discipline, needsReview: false, sourceHint: 'ISO date parsed from source text' });
  } else {
    // no deterministic date → a single line flagged for operator review (never fabricated)
    lines.push({ date: '', instructorsRequired, discipline, needsReview: true, sourceHint: 'no unambiguous date in source — operator to confirm' });
  }
  return lines;
}

// ── manual / paste adapter (the proof adapter) ──────────────────────────────────
// raw = { organizationId, seasonId, text, customer:{name,email,phone}, sourceType, sourceSystem,
//         requestedSessions?, generatedIntakeId }. No external id → ctx.generatedIntakeId is required.
export function manualAdapter(raw = {}, ctx = {}) {
  const draft = Array.isArray(raw.requestedSessions) && raw.requestedSessions.length
    ? raw.requestedSessions
    : extractRequestedSessionsDraft(raw.text || '');
  return normalizeSourceIntake({
    organizationId: raw.organizationId,
    seasonId: raw.seasonId,
    sourceType: raw.sourceType || SOURCE_TYPE.MANUAL,
    sourceSystem: raw.sourceSystem || SOURCE_SYSTEM.MANUAL,
    sourceExternalId: '', // manual paste has no stable external id → the writer assigns a generated intake id
    sourceDisplayLabel: raw.sourceDisplayLabel || 'Manual entry',
    receivedAt: raw.receivedAt || ctx.now || null,
    subject: raw.subject || '',
    normalizedText: raw.text || '',
    contact: raw.customer || {},
    requestedSessionsDraft: draft,
    notes: raw.notes || '',
    provenance: { enteredBy: str(ctx.actorName), channelLabel: str(raw.channelLabel) },
  }, { ...ctx, generatedIntakeId: raw.generatedIntakeId || ctx.generatedIntakeId });
}

// ── PRODUCTION email adapter (Pass 87) ───────────────────────────────────────────
// Maps a PROVIDER-NEUTRAL normalized email message → the SAME SourceIntakeEnvelope. The server-side
// connector (e.g. a Gmail connector in server/integrations/) is responsible for turning provider-specific
// payloads into this shape; this adapter contains NO Gmail/provider API knowledge. HTML is never rendered —
// `normalizeSourceIntake` sanitizes body → plain text. Attachments are preserved as METADATA ONLY (filename/
// mimeType/size/id) as evidence; their content is NOT downloaded or executed (§15). A missing provider
// message id leaves `sourceExternalId` empty → `normalizeSourceIntake` fails CLOSED (errors → reviewable),
// never fabricating an identity. `receivedAt` is transport time; the year/timezone are NEVER guessed into dates.
//   raw = { organizationId, seasonId, messageId, threadId, from:{name,email,phone}, to?, cc?, subject,
//           receivedAt, bodyText?, bodyHtml?, attachments?:[{filename,mimeType,size,attachmentId}], labels?:[] }
export function emailAdapter(raw = {}, ctx = {}) {
  const body = str(raw.bodyText) || str(raw.bodyHtml) || ''; // prefer text; HTML is sanitized downstream
  const attachments = (Array.isArray(raw.attachments) ? raw.attachments : []).slice(0, 25).map((a) => ({
    filename: str(a && a.filename).slice(0, 255),
    mimeType: str(a && a.mimeType).slice(0, 120),
    size: Number.isFinite(+(a && a.size)) ? Math.max(0, Math.floor(+a.size)) : null,
    attachmentId: str(a && a.attachmentId).slice(0, 256), // reference only — content NOT fetched in intake
  })).filter((a) => a.filename || a.attachmentId);
  const labels = (Array.isArray(raw.labels) ? raw.labels : []).map(str).filter(Boolean).slice(0, 20);
  const { intake, errors } = normalizeSourceIntake({
    organizationId: raw.organizationId,
    seasonId: raw.seasonId,
    sourceType: raw.sourceType || SOURCE_TYPE.DIRECT,
    sourceSystem: SOURCE_SYSTEM.EMAIL,
    sourceExternalId: str(raw.messageId),        // stable transport id → deterministic idempotency (missing → fail-closed)
    sourceConversationId: str(raw.threadId),
    sourceDisplayLabel: 'Email',
    receivedAt: raw.receivedAt || null,
    subject: raw.subject || '',
    normalizedText: body,                        // normalizeSourceIntake sanitizes HTML → plain text
    contact: { name: raw.from?.name, email: raw.from?.email, phone: raw.from?.phone },
    requestedSessionsDraft: extractRequestedSessionsDraft(body),
    provenance: {
      messageId: str(raw.messageId), threadId: str(raw.threadId),
      fromDomain: str(raw.from?.email).split('@')[1] || '',
      toCount: Array.isArray(raw.to) ? raw.to.length : 0,
      labels: labels.join(','), attachmentCount: attachments.length,
    },
  }, ctx);
  // attach attachment METADATA to the single inbound communication (evidence; never content)
  if (attachments.length && Array.isArray(intake.communications) && intake.communications[0]) {
    intake.communications[0].attachments = attachments;
  }
  return { intake, errors };
}

// Back-compat alias — the fixture adapter and the production adapter are the SAME provider-neutral mapping
// (the fixture always only proved this boundary). Existing callers/tests keep working.
export const emailFixtureAdapter = emailAdapter;

// ── partner/API fixture adapter (shape a future partner webhook would emit) ──────
// raw = { organizationId, seasonId, reservationId, customerId, guest:{name,email,phone}, sessions:[{date,instructors,discipline}], receivedAt }
export function partnerApiFixtureAdapter(raw = {}, ctx = {}) {
  const sessions = Array.isArray(raw.sessions) ? raw.sessions.map((s, i) => ({
    lineId: `line-${i + 1}`, date: str(s.date), instructorsRequired: s.instructors || s.instructorsRequired || 1,
    discipline: str(s.discipline), bookingType: str(s.bookingType), location: str(s.location), needsReview: !str(s.date),
  })) : [];
  return normalizeSourceIntake({
    organizationId: raw.organizationId,
    seasonId: raw.seasonId,
    sourceType: raw.sourceType || SOURCE_TYPE.PARTNER,
    sourceSystem: SOURCE_SYSTEM.PARTNER_API,
    sourceExternalId: str(raw.reservationId),
    sourceCustomerId: str(raw.customerId),       // trusted external customer id → Pass-63 EXPLICIT evidence
    sourceDisplayLabel: str(raw.partnerName) || 'Partner API',
    receivedAt: raw.receivedAt || null,
    subject: str(raw.subject) || `Reservation ${str(raw.reservationId)}`,
    normalizedText: str(raw.notes),
    contact: { name: raw.guest?.name, email: raw.guest?.email, phone: raw.guest?.phone },
    requestedSessionsDraft: sessions,
    provenance: { reservationId: str(raw.reservationId), customerId: str(raw.customerId), partner: str(raw.partnerName) },
  }, ctx);
}

// Registry so a caller can dispatch by system name without the core knowing any adapter internals.
export const SOURCE_ADAPTERS = {
  [SOURCE_SYSTEM.MANUAL]: manualAdapter,
  [SOURCE_SYSTEM.EMAIL]: emailAdapter,
  [SOURCE_SYSTEM.PARTNER_API]: partnerApiFixtureAdapter,
};
